import React, { useState, useMemo } from 'react';
import { ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';

/**
 * Reusable searchable dropdown: user can type to filter options, then select one.
 *
 * @param {Object} props
 * @param {{ value: string|number, label: string }[]} props.options - List of { value, label }. Label is shown and used for search.
 * @param {string|number} props.value - Currently selected value (must match option.value).
 * @param {(value: string|number) => void} props.onChange - Called when user selects an option.
 * @param {string} [props.placeholder='Select...'] - Shown on trigger when nothing selected.
 * @param {boolean} [props.disabled=false]
 * @param {string} [props.searchPlaceholder='Type to search...'] - Placeholder inside the search input.
 * @param {string} [props.emptyMessage='No results found.'] - Shown when filter has no matches.
 * @param {string} [props.triggerClassName] - Extra class names for the trigger button.
 * @param {string} [props.triggerTestId] - data-testid for the trigger (e.g. for tests).
 * @param {string} [props.contentClassName] - Extra class names for the popover content.
 */
export function SearchableDropdownBox({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  disabled = false,
  searchPlaceholder = 'Type to search...',
  emptyMessage = 'No results found.',
  triggerClassName = '',
  triggerTestId,
  contentClassName = '',
}) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(
    () => options.find(opt => String(opt.value) === String(value)),
    [options, value]
  );
  const displayLabel = selected ? String(selected.label || '').replace(/\s+/g, ' ').trim() : '';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={
            'flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 [&>span]:truncate ' +
            triggerClassName
          }
          disabled={disabled}
          data-testid={triggerTestId}
        >
          <span className={displayLabel ? 'text-foreground' : 'text-muted-foreground'} title={displayLabel || undefined}>
            {displayLabel || placeholder}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className={'w-[var(--radix-popover-trigger-width)] p-0 ' + contentClassName}
        align="start"
      >
        <Command
          shouldFilter={true}
          filter={(value, search) => {
            if (!search || !search.trim()) return 1;
            const s = search.toLowerCase().trim();
            const v = String(value || '').toLowerCase();
            return v.includes(s) ? 1 : 0;
          }}
        >
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {options.map(opt => {
                const label = String(opt.label || '').replace(/\s+/g, ' ').trim();
                return (
                  <CommandItem
                    key={String(opt.value)}
                    value={`${label} ${opt.value}`}
                    onSelect={() => {
                      onChange(opt.value);
                      setOpen(false);
                    }}
                  >
                    {label}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
